/**
 * PaymentCancelPage - v1.0 NOWPayments Cancel Handler
 * Shown when user cancels checkout or payment fails
 *
 * Route: /q-ai/payments/cancel
 *
 * Features:
 * - Clear messaging about what happened
 * - Retry option to purchase again
 * - Return to Q.AI Coach
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QAIHeader from '../../Components/QAI/QAIHeader';
import { getPendingPayment, clearPendingPayment } from '../../api/paymentApi';
import { SUPPORT_EMAIL } from '../../config';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS (Matching QAILandingPage)
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
    primary: '#27B56D',
    primaryLight: '#22C55E',
    background: '#0A0A0F',
    surface: '#12121A',
    text: '#FFFFFF',
    textSecondary: '#E2E8F0',
    textMuted: '#94A3B8',
    border: 'rgba(255, 255, 255, 0.08)',
    error: '#EF4444',
    warning: '#FBBF24'
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const PaymentCancelPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [pendingPayment, setPendingPayment] = useState(null);

    // Get reason from URL if provided
    const reason = searchParams.get('reason');

    useEffect(() => {
        // Check if there was a pending payment
        const pending = getPendingPayment();
        if (pending) {
            setPendingPayment(pending);
            console.log('[PaymentCancelPage] Found pending payment:', pending);
        }
    }, []);

    // ═══════════════════════════════════════════════════════════════════════════
    // HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    const handleRetryPurchase = () => {
        // Clear the old pending payment
        clearPendingPayment();
        // Navigate to coach page - the pricing modal can be reopened there
        navigate('/q-ai/coach', { state: { openPricing: true } });
    };

    const handleGoToCoach = () => {
        // Clear pending payment
        clearPendingPayment();
        navigate('/q-ai/coach');
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: BRAND.background,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Unified Q.AI Header */}
            <QAIHeader
                showTokens={true}
                showHelp={false}
                showSettings={false}
                showHomeButton={true}
            />

            {/* Page Content */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3
                }}
            >
            {/* Main Card */}
            <Paper
                elevation={0}
                sx={{
                    background: BRAND.surface,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 3,
                    p: { xs: 3, md: 5 },
                    maxWidth: 480,
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                {/* Icon */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3
                    }}
                >
                    <CloseIcon sx={{ fontSize: 40, color: BRAND.error }} />
                </Box>

                {/* Title */}
                <Typography
                    variant="h4"
                    sx={{
                        color: BRAND.text,
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { xs: '1.5rem', md: '1.75rem' }
                    }}
                >
                    Payment Cancelled
                </Typography>

                {/* Message */}
                <Typography
                    sx={{
                        color: BRAND.textMuted,
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        mb: 3
                    }}
                >
                    {reason === 'expired'
                        ? 'Your payment session has expired. No charges were made.'
                        : 'Your payment was cancelled. No charges were made to your account.'}
                </Typography>

                {/* Pending Payment Info */}
                {pendingPayment && (
                    <Box
                        sx={{
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: 2,
                            p: 2,
                            mb: 3
                        }}
                    >
                        <Typography sx={{ color: BRAND.textMuted, fontSize: '0.85rem', mb: 1 }}>
                            Your selected package:
                        </Typography>
                        <Typography sx={{ color: BRAND.primary, fontSize: '1.1rem', fontWeight: 600 }}>
                            {pendingPayment.tokens} Q.AI Tokens
                        </Typography>
                    </Box>
                )}

                {/* Info Alert */}
                <Alert
                    severity="info"
                    icon={<HelpOutlineIcon />}
                    sx={{
                        mb: 3,
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#93C5FD',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        textAlign: 'left',
                        '& .MuiAlert-icon': { color: '#3B82F6' }
                    }}
                >
                    You can try again anytime. Your token balance remains unchanged.
                </Alert>

                {/* Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<RefreshIcon />}
                        onClick={handleRetryPurchase}
                        sx={{
                            backgroundColor: BRAND.primary,
                            color: '#000',
                            fontWeight: 600,
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: BRAND.primaryLight
                            }
                        }}
                    >
                        Try Again
                    </Button>

                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<ChatIcon />}
                        onClick={handleGoToCoach}
                        sx={{
                            borderColor: BRAND.border,
                            color: BRAND.textSecondary,
                            py: 1.5,
                            '&:hover': {
                                borderColor: BRAND.textMuted,
                                backgroundColor: 'rgba(255, 255, 255, 0.02)'
                            }
                        }}
                    >
                        Return to Q.AI Coach
                    </Button>
                </Box>
            </Paper>

            {/* Help text */}
            <Typography
                sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.8rem',
                    mt: 3,
                    textAlign: 'center'
                }}
            >
                Having issues? Contact support at {SUPPORT_EMAIL}
            </Typography>
            </Box>
        </Box>
    );
};

export default PaymentCancelPage;
